import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import AppContext from 'AppContext';
import GrpButton from 'Library/GrpButton/GrpButton';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import {
  getShardMappings,
  addShardMapping,
  removeShardMapping,
  getDurableFunctionStatus,
} from 'Services/backofficeApi';
import classes from './ShardManagement.module.scss';

const POLL_INTERVAL = 5000;

const getAcceptedTracking = (payload) => {
  const instanceId = payload?.Id || payload?.id || payload?.instanceId;
  const statusQueryGetUri = payload?.StatusQueryGetUri || payload?.statusQueryGetUri;

  if (!instanceId || !statusQueryGetUri) {
    return null;
  }

  return {
    instanceId,
    statusQueryGetUri,
  };
};

const ShardManagement = () => {
  const context = useContext(AppContext);
  const gridRef = useRef(null);
  const pollTimerRef = useRef(null);
  const trackingRef = useRef({});

  const [loading, setLoading] = useState(true);
  const [shardMappings, setShardMappings] = useState([]);
  const [panelMode, setPanelMode] = useState(null); // null | 'add' | 'delete'
  const [currentShard, setCurrentShard] = useState(null);

  // Add form state
  const [clientCode, setClientCode] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientType, setClientType] = useState('AssetManagement');
  const [formErrors, setFormErrors] = useState({});

  // Delete confirmation
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getShardMappings();
      const data = result.Content || result.value || result || [];
      const sorted = Array.isArray(data) ? [...data].sort((a, b) => (a.DatabaseName || '').localeCompare(b.DatabaseName || '')) : [];
      setShardMappings(sorted);
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.message || 'Failed to load shard mappings'}</> });
    } finally {
      setLoading(false);
    }
  }, [context.handlers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  }, []);

  const MAX_POLL_FAILURES = 10;

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(async () => {
      const tracking = trackingRef.current;
      const pendingIds = Object.keys(tracking).filter((id) => tracking[id].status !== 'Completed');

      if (pendingIds.length === 0) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
        return;
      }

      for (const id of pendingIds) {
        try {
          const status = await getDurableFunctionStatus(tracking[id].statusQueryGetUri);
          tracking[id].failures = 0;
          if (status.runtimeStatus === 'Completed' || status.RuntimeStatus === 'Completed') {
            tracking[id].status = 'Completed';
            await loadData();
          }
        } catch {
          tracking[id].failures = (tracking[id].failures || 0) + 1;
          if (tracking[id].failures >= MAX_POLL_FAILURES) {
            tracking[id].status = 'Completed'; // give up, stop polling this id
            await loadData(); // refresh grid to clear the pending row
          }
        }
      }
    }, POLL_INTERVAL);
  }, [loadData]);

  const closePanel = useCallback(() => {
    setPanelMode(null);
    setCurrentShard(null);
    setClientCode('');
    setClientName('');
    setClientType('AssetManagement');
    setFormErrors({});
    setDeleteConfirmText('');
  }, []);

  // ─── Add Shard ──────────────────────────────────────────────────────

  const handleAddClick = useCallback(() => {
    setPanelMode('add');
  }, []);

  const validateAddForm = () => {
    const errors = {};
    if (!clientCode.trim()) errors.clientCode = 'Client code is required';
    else if (clientCode.trim().length > 7) errors.clientCode = 'Maximum 7 characters';
    if (!clientName.trim()) errors.clientName = 'Client name is required';
    else if (clientName.trim().length > 255) errors.clientName = 'Maximum 255 characters';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddSubmit = useCallback(async () => {
    if (!validateAddForm()) return;

    const dbName = `PRIIPS_${clientCode.trim()}`;

    // Optimistic: add pending row
    setShardMappings((prev) =>
      [...prev, { DatabaseName: dbName, IsBeingCreated: true, Clients: null }]
        .sort((a, b) => (a.DatabaseName || '').localeCompare(b.DatabaseName || '')),
    );
    closePanel();

    try {
      const payload = {
        ClientCode: clientCode.trim(),
        DatabaseName: dbName,
        UseElasticPool: true,
        ClientName: clientName.trim(),
        ClientType: clientType === 'AssetManagement' ? 'AM' : 'INS',
        NotificationSettings: {
          NotificationScope: 'SendingUser',
          Messages: [
            { Title: 'Request Submitted', TextTemplate: '', NotificationEvent: 'Start', NotificationSeverity: 'Information' },
            { Title: 'Request Succeeded', TextTemplate: '', NotificationEvent: 'Success', NotificationSeverity: 'Success' },
            { Title: 'Request Failed', TextTemplate: '', NotificationEvent: 'Failure', NotificationSeverity: 'Error' },
          ],
        },
      };

      const accepted = await addShardMapping(payload);
      const tracking = getAcceptedTracking(accepted);

      if (tracking) {
        trackingRef.current[tracking.instanceId] = {
          instanceId: tracking.instanceId,
          databaseName: dbName,
          statusQueryGetUri: tracking.statusQueryGetUri,
          status: 'Unknown',
          failures: 0,
        };
        startPolling();
      } else {
        // Backend accepted the request but returned no durable tracking info.
        // Refresh immediately so the optimistic row is replaced with real data.
        await loadData();
      }
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.message || 'Failed to add shard'}</> });
      // Remove optimistic row
      setShardMappings((prev) => prev.filter((s) => s.DatabaseName !== dbName));
    }
  }, [clientCode, clientName, clientType, closePanel, startPolling, context.handlers]);

  // ─── Delete Shard ───────────────────────────────────────────────────

  const handleDeleteClick = useCallback((shard) => {
    setCurrentShard(shard);
    setPanelMode('delete');
  }, []);

  const handleDeleteSubmit = useCallback(async () => {
    if (!currentShard || deleteConfirmText !== currentShard.DatabaseName) return;

    // Remove from grid immediately
    setShardMappings((prev) => prev.filter((s) => s.DatabaseName !== currentShard.DatabaseName));
    const shardToDelete = currentShard;
    closePanel();

    try {
      const clients = shardToDelete.Clients || [];
      for (const client of clients) {
        const payload = {
          ClientCode: client.ClientCode,
          DatabaseName: shardToDelete.DatabaseName,
          DropDatabase: true,
        };
        const accepted = await removeShardMapping(payload);
        const tracking = getAcceptedTracking(accepted);

        if (tracking) {
          trackingRef.current[tracking.instanceId] = {
            instanceId: tracking.instanceId,
            databaseName: shardToDelete.DatabaseName,
            statusQueryGetUri: tracking.statusQueryGetUri,
            status: 'Unknown',
          };
        }
      }
      startPolling();
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.message || 'Failed to delete shard'}</> });
      await loadData(); // Reload to restore
    }
  }, [currentShard, deleteConfirmText, closePanel, startPolling, context.handlers, loadData]);

  // ─── Grid Columns ──────────────────────────────────────────────────

  const columnDefs = [
    {
      headerName: 'Database Name',
      field: 'DatabaseName',
      flex: 1,
      filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data.IsBeingCreated) {
          return (
            <span className={classes.pendingCell}>
              <span className={classes.spinner} />
              <i>{params.value} (pending)</i>
            </span>
          );
        }
        return params.value;
      },
    },
    {
      headerName: 'Client',
      flex: 2,
      filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        const clients = params.data.Clients;
        if (!clients || clients.length === 0) return 'N/A';
        return clients.map((c) => `${c.ClientCode} - ${c.ClientName} (${c.ClientType})`).join(', ');
      },
    },
    {
      headerName: 'Latest Flyway Run',
      flex: 1,
      valueGetter: (params) => {
        const completedOn = params.data.FlywayTrackingData?.InstallCompletedOn;
        if (!completedOn) return 'N/A';
        const elapsed = Date.now() - new Date(completedOn).getTime();
        const hours = Math.floor(elapsed / 3600000);
        const days = Math.floor(hours / 24);
        if (days > 0) return `${days}d ${hours % 24}h ago`;
        if (hours > 0) return `${hours}h ago`;
        const mins = Math.floor(elapsed / 60000);
        return `${mins}m ago`;
      },
    },
    {
      headerName: 'Latest V Script',
      field: 'FlywayTrackingData.LatestVScriptVersion',
      width: 160,
    },
    {
      headerName: '',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params) =>
        !params.data.IsBeingCreated ? (
          <GrpButton size="Small" icon={['fas', 'trash']} iconSide="Round" onClick={() => handleDeleteClick(params.data)} />
        ) : null,
    },
  ];

  if (loading && shardMappings.length === 0) return <GrpSpinner />;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h3>Shard Management</h3>
        <GrpButton size="Small" type="Primary" text="Add New Shard" icon={['fas', 'plus']} onClick={handleAddClick} />
      </div>

      <div className={`ag-theme-balham ${classes.gridContainer}`}>
        <AgGridReact
          ref={gridRef}
          rowData={shardMappings}
          columnDefs={columnDefs}
          domLayout="normal"
          defaultColDef={{ sortable: true, resizable: true }}
          getRowId={(params) => params.data.DatabaseName}
          suppressRowClickSelection
        />
      </div>

      {/* Side panel */}
      {panelMode && (
        <>
          <div className={classes.panelBackdrop} onClick={closePanel} />
          <div className={classes.panelOverlay}>
            {panelMode === 'add' && (
              <>
                <div className={classes.panelHeader}>Add a New Shard</div>
                <div className={classes.formGroup}>
                  <label>Client Code</label>
                  <input value={clientCode} onChange={(e) => setClientCode(e.target.value)} maxLength={7} />
                  {formErrors.clientCode && <span className={classes.error}>{formErrors.clientCode}</span>}
                </div>
                <div className={classes.formGroup}>
                  <label>Client Name</label>
                  <input value={clientName} onChange={(e) => setClientName(e.target.value)} maxLength={255} />
                  {formErrors.clientName && <span className={classes.error}>{formErrors.clientName}</span>}
                </div>
                <div className={classes.formGroup}>
                  <label>Client Type</label>
                  <select value={clientType} onChange={(e) => setClientType(e.target.value)}>
                    <option value="AssetManagement">Asset Management</option>
                    <option value="Insurance">Insurance</option>
                  </select>
                </div>
                <div className={classes.panelActions}>
                  <GrpButton size="Small" type="Primary" text="Submit" onClick={handleAddSubmit} />
                  <GrpButton size="Small" type="Secondary" text="Cancel" onClick={closePanel} />
                </div>
              </>
            )}

            {panelMode === 'delete' && currentShard && (
              <>
                <div className={classes.panelHeader}>Delete {currentShard.DatabaseName}</div>
                <div className={classes.warningBanner}>
                  <span className={classes.warningIcon}>&#9888;</span>
                  <p>
                    Warning! Deleting {currentShard.DatabaseName} is irreversible. The action you&apos;re about to
                    take can&apos;t be undone. Going further will delete it and all the items in it permanently.
                  </p>
                </div>
                <div className={classes.confirmInput}>
                  <p>Type the SQL database name</p>
                  <input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} />
                </div>
                <div className={classes.panelActions}>
                  <GrpButton
                    size="Small"
                    type="Primary"
                    text="Delete"
                    onClick={handleDeleteSubmit}
                    disabled={deleteConfirmText !== currentShard.DatabaseName}
                  />
                  <GrpButton size="Small" type="Secondary" text="Cancel" onClick={closePanel} />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ShardManagement;
